import type { FormField, FieldType } from '../types/forms';

const NON_NAVIGABLE_TYPES: FieldType[] = ['welcome_screen', 'thank_you_screen', 'heading', 'paragraph'];

/**
 * Validates a single field value against its validation rules.
 * Returns an error message string or null if valid.
 */
export function validateFieldValue(field: FormField, value: any): string | null {
  const isEmpty = !value || (Array.isArray(value) && value.length === 0);

  // Required check
  if (field.required && isEmpty) {
    return 'Este campo é obrigatório';
  }

  // Skip further validation if empty and not required
  if (isEmpty) return null;

  const strValue = String(value);

  // Type-specific validation
  if (field.type === 'email') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(strValue)) return 'Email inválido';
  }

  if (field.type === 'phone') {
    const digits = strValue.replace(/\D/g, '');
    if (digits.length > 0 && digits.length !== 10 && digits.length !== 11) {
      return 'Telefone inválido. Use DDD + número';
    }
  }

  // Validation rules from field.validation
  const v = field.validation;
  if (!v) return null;

  // minLength / maxLength (text fields)
  if (v.minLength !== undefined && strValue.length < v.minLength) {
    return `Mínimo de ${v.minLength} caracteres`;
  }
  if (v.maxLength !== undefined && strValue.length > v.maxLength) {
    return `Máximo de ${v.maxLength} caracteres`;
  }

  // min / max (number fields)
  if (field.type === 'number' && typeof value === 'number') {
    if (v.min !== undefined && value < v.min) {
      return `Valor mínimo: ${v.min}`;
    }
    if (v.max !== undefined && value > v.max) {
      return `Valor máximo: ${v.max}`;
    }
  }

  // pattern (regex)
  if (v.pattern) {
    try {
      const regex = new RegExp(v.pattern);
      if (!regex.test(strValue)) {
        return v.patternMessage || 'Formato inválido';
      }
    } catch {
      // Invalid regex — skip validation
    }
  }

  return null;
}

export interface PageValidationError {
  fieldId: string;
  error: string;
}

/**
 * Validates all fields on a page sequentially (in order).
 * Returns the first field with an error, or null if all valid.
 * Skips non-navigable types (headings, paragraphs, screens).
 */
export function validatePageSequentially(
  fields: FormField[],
  formData: Record<string, any>
): PageValidationError | null {
  for (const field of fields) {
    if (NON_NAVIGABLE_TYPES.includes(field.type)) continue;

    const error = validateFieldValue(field, formData[field.id]);
    if (error) {
      return { fieldId: field.id, error };
    }
  }
  return null;
}
